import {useCallback, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode} from 'react'
import './ConverterShared.css'
import './ConverterDropZone.css'

interface ConverterDropZoneProps {
  accept: string
  busy: boolean
  hasFile: boolean
  pickLabel: string
  replaceLabel: string
  clearLabel: string
  busyLabel: string
  hint: string
  error: string | null
  onFileSelected: (file: File) => void
  onClear: () => void
  /** Optional preview content (thumbnail, video element, etc.) rendered above the actions */
  children?: ReactNode
}

/** Generic drag-and-drop + hidden-input upload zone shared by the image-converter and video-to-gif tools. */
const ConverterDropZone = ({
  accept,
  busy,
  hasFile,
  pickLabel,
  replaceLabel,
  clearLabel,
  busyLabel,
  hint,
  error,
  onFileSelected,
  onClear,
  children,
}: ConverterDropZoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (file) onFileSelected(file)
    },
    [onFileSelected],
  )

  const handlePick = useCallback(() => inputRef.current?.click(), [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) onFileSelected(file)
    },
    [onFileSelected],
  )

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => setDragOver(false), [])

  return (
    <div
      className={dragOver ? 'cvt-dropzone is-dragover' : 'cvt-dropzone'}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {children}

      <div className="cvt-dropzone-actions">
        <button type="button" className="cvt-btn cvt-btn-secondary" onClick={handlePick} disabled={busy}>
          {busy ? busyLabel : hasFile ? replaceLabel : pickLabel}
        </button>
        {hasFile && (
          <button type="button" className="cvt-btn cvt-btn-ghost" onClick={onClear} disabled={busy}>
            {clearLabel}
          </button>
        )}
      </div>

      <input ref={inputRef} type="file" accept={accept} onChange={handleChange} style={{display: 'none'}} />

      <p className="cvt-hint">{hint}</p>
      {error && <p className="cvt-error">{error}</p>}
    </div>
  )
}

export default ConverterDropZone
